import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Navbar from "../../components/layout/Navbar";
import Footer from "../../components/layout/Footer";
import UclSection from "../../components/layout/UclSection";
import HeroBanner from "../../components/layout/HeroBanner";
import { fetchUserBanners, fetchMovieDetail, fetchTrailer, fetchGenres, fetchAgeRatings, fetchTvStoreCategories, fetchTvStoreProducts, getImageUrl } from "../../redux/content";
import "../../components/styles/discoveryPage.scss";

/**
 * DiscoveryPage — Subscriptions Discovery screen
 * Figma: Subscriptions (Discovery) (25460:26921) — 1920×3714
 *
 * Sections:
 *   1. Hero banner (shared component)
 *   2. Subscription category cards
 *   3. UCL Football section (reusable component)
 *   4. Showbox promo section
 *   5. Footer
 */

// Fallback gradients when background_color is null — cycle through these
const FALLBACK_GRADIENTS = [
  "linear-gradient(to right, #eab5b5 7%, #990009 100%)",
  "linear-gradient(to right, #eab5e1 10%, #8a1f8a 100%)",
  "linear-gradient(to right, #91b7f0 10%, #01417c 100%)",
  "linear-gradient(to right, #f0db91 7%, #7c5a01 100%)",
];


const DiscoveryPage = () => {
  const navigate = useNavigate();
  const banners = useSelector((state) => state.content.banners);
  const bannersLoading = useSelector((state) => state.content.loaders.banners);
  const tvStoreCategories = useSelector((state) => state.content.tvStoreCategories);
  const [heroSlides, setHeroSlides] = useState([]);
  const [slidesBuilding, setSlidesBuilding] = useState(true);

  // Fetch banners + TV Store categories on mount
  useEffect(() => {
    if (banners.length === 0) {
      console.log("[DiscoveryPage] Fetching banners...");
      fetchUserBanners("DESKTOP");
    }
    if (tvStoreCategories.length === 0) {
      console.log("[DiscoveryPage] Fetching TV Store categories...");
      fetchTvStoreCategories();
    }
  }, [banners.length, tvStoreCategories.length]);

  // Fetch SVOD product details for subscription cards
  const svodCategory = tvStoreCategories.find((c) => c.uid === "svod");
  const [svodProducts, setSvodProducts] = useState([]);
  const [svodProductsLoading, setSvodProductsLoading] = useState(true);

  useEffect(() => {
    if (!svodCategory?.tv_store?.length) return;

    const loadProducts = async () => {
      setSvodProductsLoading(true);
      console.log("[DiscoveryPage] Fetching SVOD product details for", svodCategory.tv_store.length, "products");
      const products = await fetchTvStoreProducts(svodCategory.tv_store);
      setSvodProducts(products);
      setSvodProductsLoading(false);
      console.log("[DiscoveryPage] SVOD products loaded:", products.map((p) => `${p.uid}:${p.name}(card:${p.card_image_id})`).join(", "));
    };

    loadProducts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [svodCategory?.tv_store?.length]);

  // Build hero slides from VOD banners + resolve movie metadata
  const vodBanners = banners.filter((b) => b.type === "VOD");

  useEffect(() => {
    if (vodBanners.length === 0) return;

    const loadSlides = async () => {
      console.log("[DiscoveryPage] Building hero slides for", vodBanners.length, "VOD banners");
      const [genreMap, ageRatingMap] = await Promise.all([
        fetchGenres(),
        fetchAgeRatings(),
      ]);

      const slides = await Promise.all(
        vodBanners.map(async (banner) => {
          const slide = {
            bgImage: banner.preview_image_id
              ? getImageUrl({ image_id: banner.preview_image_id })
              : null,
            title: banner.title || null,
            description: banner.description || null,
            playHref: banner.content_id
              ? `/player/${banner.link || banner.content_id}?type=movie`
              : null,
            // Pass numeric content_id through nav state so VideoPlayerPage
            // can hit the /movies/{id} detail endpoint (UID returns 502).
            playState: banner.content_id ? { movieId: banner.content_id } : null,
            detailsHref: banner.content_id
              ? `/details/${banner.content_id}`
              : null,
            trailerUrl: null,
            rating: null,
            rated: null,
            year: null,
            genres: null,
          };

          // Resolve metadata from linked movie if content_id exists
          if (banner.content_id) {
            try {
              const movie = await fetchMovieDetail(banner.content_id);
              if (movie) {
                slide.rating = movie.critics_rating ? String(movie.critics_rating) : null;
                slide.year = movie.year ? String(movie.year) : null;
                slide.rated = movie.age_rating_id ? ageRatingMap[movie.age_rating_id] || null : null;
                const genreIds = movie.movie_genres || movie.genres || [];
                slide.genres = genreIds.length > 0
                  ? genreIds.map((g) => genreMap[g.id || g] || "").filter(Boolean)
                  : null;
                // Use movie description if banner description is short/generic
                if (!slide.description || slide.description.length < 30) {
                  slide.description = movie.description || slide.description;
                }
                // Title art: prefer flat title_art_image_id, fall back to movie_image_stores[type=TITLE_ART]
                const titleArtStoreId = Array.isArray(movie.movie_image_stores)
                  ? movie.movie_image_stores.find((s) => s.type === "TITLE_ART")?.image_store_id
                  : null;
                const titleArtId = movie.title_art_image_id || titleArtStoreId;
                if (titleArtId) {
                  slide.titleArt = getImageUrl({ image_id: titleArtId });
                }
                // Fetch trailer URL — check cm_trailer or trailer_url_ott fields
                if (movie.cm_trailer || movie.trailer_url_ott || movie.has_trailer) {
                  const trailerUrl = await fetchTrailer(banner.content_id, "movie");
                  if (trailerUrl) {
                    slide.trailerUrl = trailerUrl;
                    console.log("[DiscoveryPage] Trailer found for:", banner.title, trailerUrl.substring(0, 60));
                  }
                }
              }
            } catch (e) {
              console.warn("[DiscoveryPage] Failed to load movie meta for banner:", banner.title, e.message);
            }
          }

          return slide;
        })
      );

      console.log("[DiscoveryPage] Hero slides built:", slides.map((s) => s.title).join(", "));
      setHeroSlides(slides);
      setSlidesBuilding(false);
    };

    loadSlides();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vodBanners.length]);

  console.log("[DiscoveryPage] rendering, svodProducts:", svodProducts.length, "heroSlides:", heroSlides.length, "productsLoading:", svodProductsLoading);

  // Build the navigate slug + handler shared by cards and promo banners
  const goToProduct = (product) => {
    const nameSlug = (product.name || product.uid)
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
    console.log("[DiscoveryPage] Product clicked:", product.uid, product.name, "→ /discover/product/" + product.id + "/" + nameSlug);
    navigate(`/discover/product/${product.id}/${nameSlug}`);
  };

  return (
    <div className="discovery-page">
      <Navbar activeTab="Discover" />

      {/* Hero Banner — carousel of VOD banners, loader until slides are built */}
      <HeroBanner
        loading={(bannersLoading || slidesBuilding) && heroSlides.length === 0}
        banners={heroSlides.length > 0 ? heroSlides : undefined}
        fromTab="Discover"
      />

      {/* Content */}
      <div className="discovery-page__content">
        {/* Subscription Product Cards — individual SVOD products */}
        <section className="discovery-page__subs">
          <div className="discovery-page__subs-row">
            {svodProductsLoading && svodProducts.length === 0 &&
              Array.from({ length: 4 }).map((_, i) => (
                <div key={`skel-${i}`} className="discovery-page__subs-card discovery-page__subs-card--skeleton">
                  <div className="discovery-page__subs-shimmer" />
                </div>
              ))
            }
            {svodProducts.map((product, i) => {
              const bg = FALLBACK_GRADIENTS[i % FALLBACK_GRADIENTS.length];

              console.log("[DiscoveryPage] Product card:", product.uid, product.name);

              return (
                <div
                  key={product.id || i}
                  className="discovery-page__subs-card"
                  style={{
                    background: bg,
                  }}
                  onClick={() => goToProduct(product)}
                >
                  <span className="discovery-page__subs-label">
                    {product.name ? (
                      <>Subscribe to<br />{product.name}</>
                    ) : (
                      product.uid
                    )}
                  </span>
                </div>
              );
            })}
          </div>
          {!svodProductsLoading && svodProducts.length === 0 && tvStoreCategories.length > 0 && (
            <p style={{ color: "#a3a3a3", padding: "24px 0 0 80px" }}>No subscription products available</p>
          )}
        </section>

        {/* UCL Section */}
        {/* Temporarily hidden per client */}
        {false && <UclSection />}

        {/* Promo Banners — one full-width banner per SVOD product */}
        {svodProducts.length > 0 && (
          <section className="discovery-page__promos">
            {svodProducts.map((product, i) => {
              const cardImage = product.card_image_id
                ? getImageUrl({ image_id: product.card_image_id })
                : null;
              return (
                <div
                  key={product.id || i}
                  className="discovery-page__showbox"
                  onClick={() => goToProduct(product)}
                  role="button"
                  tabIndex={0}
                  style={{ cursor: "pointer" }}
                >
                  <div
                    className="discovery-page__showbox-bg"
                    style={cardImage ? undefined : { background: FALLBACK_GRADIENTS[i % FALLBACK_GRADIENTS.length] }}
                  >
                    {cardImage && (
                      <img src={cardImage} alt="" className="discovery-page__showbox-bg-img" />
                    )}
                    <div className="discovery-page__showbox-overlay" />
                  </div>
                  <div className="discovery-page__showbox-content">
                    {product.name && (
                      <h2 className="discovery-page__showbox-title">{product.name}</h2>
                    )}
                    <button
                      className="discovery-page__showbox-btn"
                      onClick={(e) => { e.stopPropagation(); goToProduct(product); }}
                    >
                      <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                        <path d="M6 4L16 10L6 16V4Z" fill="#0a0a0a" />
                      </svg>
                      Subscribe Now
                    </button>
                  </div>
                </div>
              );
            })}
          </section>
        )}

        <Footer />
      </div>
    </div>
  );
};

export default DiscoveryPage;
